import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { keyBy } from 'lodash';
import cns from 'classnames';

import BaseDefaultFieldsForm from './BaseDefaultFieldsForm';
import { LEARNING_ASSET_TYPES } from '../../../../../shared/Constants';

import FormFeedback from '../../../../../shared/components/formComponents/FormFeedback';

// Inputs
import BasicText from '../../../../../shared/components/formComponents/groups/BasicText';
import BasicNumericInput from '../../../../../shared/components/formComponents/groups/BasicNumericInput';
import BasicSelect from '../../../../../shared/components/formComponents/groups/BasicSelect';
import BasicToggle from '../../../../../shared/components/formComponents/groups/BasicToggle';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../../../shared/components/VerbButton';

class SectionItemDefaultFields extends BaseDefaultFieldsForm {
    static propTypes = {
        className: PropTypes.string,
        getFormValue: PropTypes.func.isRequired,
        changeFormValue: PropTypes.func.isRequired,
        localSectionItemmemberReference: PropTypes.string,
        sectionItemType: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        selectedSectionId: PropTypes.string,
        isGlobalSectionItem: PropTypes.bool,
        hideForContentAuthor: PropTypes.bool,
        isWrapUpSectionItem: PropTypes.bool
    };

    constructor(props) {
        super(props);
        this.state = {};
    }

    handleGlobalSectionItemSelect = (e, globalSectionItemId) => {
        const { globalSectionItems, populateGlobalSectionItemData } = this.props;
        const globalSectionItemsById = keyBy(globalSectionItems, '_id');
        populateGlobalSectionItemData(globalSectionItemsById[globalSectionItemId]);
    };

    _getTypeOptionsForLearningAsset = (type) => {
        if (type === LEARNING_ASSET_TYPES.WELCOME) {
            return BaseDefaultFieldsForm.welcomeTypeOptions;
        }
        if (type === LEARNING_ASSET_TYPES.SINGLE_PAGE_ACTIVITY) {
            return BaseDefaultFieldsForm.singlePageActivityTypeOptions;
        }
        if (type === LEARNING_ASSET_TYPES.MOBILE) {
            return BaseDefaultFieldsForm.mobileTypeOptions;
        }
        return BaseDefaultFieldsForm.typeOptions;
    };

    _filterTypeOptions = (types) => {
        const optionsToExclude = [
            'File Upload',
            'Email',
            'Onscreen',
            'Invite Users',
            'End Session',
            'Review Previous Activities',
            'Advanced',
            'Nudge'
        ];
        return types.filter((type) => !optionsToExclude.includes(type.name));
    };

    _renderToggles = (disabledFields) => {
        const { localSectionItemMemberReference } = this.props;
        return (
            <>
                <Field
                    name={`${localSectionItemMemberReference}.isIncludedInDiscussionGuide`}
                    label={'REFERENCE'}
                    className={cns('col', 'ms-auto')}
                    disabled={disabledFields.isActive}
                    component={BasicToggle}
                    id={'is-included-in-discussion-guide-toggle'}
                />
                <Field
                    name={`${localSectionItemMemberReference}.isActive`}
                    label={'Active?'}
                    className={cns('col-md-1', 'ms-auto')}
                    disabled={disabledFields.isActive}
                    component={BasicToggle}
                    id={'section-item-default-fields-is-active-toggle'}
                />
                <Field
                    name={`${localSectionItemMemberReference}.isRequired`}
                    label={'Required?'}
                    disabled={disabledFields.isRequired}
                    className={cns('col-md-1', 'ms-auto')}
                    component={BasicToggle}
                    id={'section-item-default-is-required-toggle'}
                />
                <Field
                    name={`${localSectionItemMemberReference}.isVisible`}
                    label={'Show in Nav?'}
                    className={cns('col', 'ms-auto', { hidden: !this.isCourseType() })}
                    component={BasicToggle}
                    id={'section-item-default-is-visible-toggle'}
                />
            </>
        );
    };

    render() {
        const {
            localSectionItemMemberReference,
            sectionItemType,
            type,
            className,
            globalSectionItems,
            isGlobalSectionItem,
            globalSectionItemId,
            globalAddSuccessMessage,
            globalSubmitSucceeded,
            hideForContentAuthor,
            isWrapUpSectionItem
        } = this.props;

        const disabledFields = this._getDisabled(localSectionItemMemberReference, sectionItemType);
        const isSolo =
            type === LEARNING_ASSET_TYPES.TRACK ||
            type === LEARNING_ASSET_TYPES.IMPACT_PROJECT ||
            type === LEARNING_ASSET_TYPES.FEEDBACK ||
            type === LEARNING_ASSET_TYPES.VIDEO ||
            type === LEARNING_ASSET_TYPES.ARTICLE;

        const targetParticipantRoleDataSource = this._buildTargetParticipantRoleDataSource(isSolo);
        const unfilteredTypeOptions = this._getTypeOptionsForLearningAsset(type);

        const typeOptions = hideForContentAuthor
            ? this._filterTypeOptions(unfilteredTypeOptions)
            : unfilteredTypeOptions;
        return (
            <div className={cns('col-md-12', className)}>
                <div className={'row'}>
                    {!hideForContentAuthor && (
                        <>
                            <Field
                                name={`${localSectionItemMemberReference}.globalSectionItemId`}
                                label={'Global Content Template'}
                                className={'col-8'}
                                options={globalSectionItems}
                                valueKey={'_id'}
                                labelKey={'name'}
                                isClearable={true}
                                component={BasicSelect}
                                onChange={this.handleGlobalSectionItemSelect}
                            />
                            {globalSectionItemId ? (
                                <div className={cns('col-md-1', 'sectionItemFieldButton')}>
                                    <VerbButton
                                        buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                                        fontAwesomeIcon={'fa-pencil'}
                                        type={'button'}
                                        href={`/editglobalcontent/${globalSectionItemId}`}
                                        newTab={true}
                                    />
                                </div>
                            ) : null}
                        </>
                    )}
                    {!hideForContentAuthor && this._renderToggles(disabledFields)}
                </div>
                {globalSubmitSucceeded && (
                    <div style={{ paddingLeft: '0px', paddingBottom: '20px', height: '35px' }}>
                        <FormFeedback
                            className={cns('col-md-12 formFeedback')}
                            successMsg={globalAddSuccessMessage}
                            submitSucceeded={globalSubmitSucceeded}
                            submitFailed={!globalSubmitSucceeded}
                        />
                    </div>
                )}
                <div className={'row'}>
                    <Field
                        name={`${localSectionItemMemberReference}.type`}
                        label={'Type'}
                        options={typeOptions}
                        disabled={disabledFields.type || isGlobalSectionItem}
                        className={'col-md-3'}
                        onChange={this._onSectionItemTypeChange.bind(this, localSectionItemMemberReference)}
                        component={BasicSelect}
                        valueKey={'name'}
                        labelKey={'label'}
                        optionRenderer={this._renderValue}
                    />
                    {isWrapUpSectionItem ? (
                        <Field
                            name={`${localSectionItemMemberReference}.name`}
                            label={'Name'}
                            className={'col-md-5'}
                            placeholder={'Wrap Up'}
                            disabled={false}
                            component={BasicText}
                        />
                    ) : (
                        <Field
                            name={`${localSectionItemMemberReference}.name`}
                            label={'Name'}
                            className={'col-md-5'}
                            placeholder={'Section Item Name'}
                            disabled={disabledFields.name}
                            component={BasicText}
                        />
                    )}
                    {hideForContentAuthor && this._renderToggles(disabledFields)}
                </div>
                {!isWrapUpSectionItem && (
                    <div className={'row'}>
                        <Field
                            component={BasicText}
                            className={'col-md-8'}
                            label={'Graphics Notes'}
                            placeholder={'Enter notes for graphics'}
                            name={`${localSectionItemMemberReference}.graphicNotes`}
                        />
                    </div>
                )}
                <div className={'row'}>
                    <Field
                        name={`${localSectionItemMemberReference}.startsOn`}
                        label={'Starts On'}
                        className={cns('col-md-3', { hidden: !this.isCourseType() })}
                        minValue={1}
                        formatFunction={this._startsOnFormatter}
                        parseFunction={this._startsOnParser}
                        disabled={disabledFields.startsOn}
                        component={BasicNumericInput}
                    />
                    <Field
                        name={`${localSectionItemMemberReference}.startsAfter`}
                        label={'Starts After'}
                        className={cns('col-md-3', { hidden: !this.isCourseType() })}
                        options={BaseDefaultFieldsForm.startTypes}
                        disabled={disabledFields.startsAfter}
                        component={BasicSelect}
                    />
                    <Field
                        name={`${localSectionItemMemberReference}.targetParticipantRole`}
                        label={'Targeted Participant'}
                        className={cns('col-md-6')}
                        disabled={disabledFields.targetParticipantRole}
                        options={targetParticipantRoleDataSource}
                        component={BasicSelect}
                    />
                </div>
            </div>
        );
    }
}

export default SectionItemDefaultFields;
